import { TablePaginationConfig } from "antd";
import { passengerWalletsApi } from "api/passengerWallet";
import { useDataLoadingContext } from "contexts/DataLoadingContext";
import { useNotificationContext } from "contexts/NotificationContext";
import { ITableData, defaultTableData } from "models";
import { IPassengerWallet } from "models/passengerWallet";
import { ChangeEvent, useEffect, useState } from "react";

export default function usePassengerWallet() {
  const { showError } = useNotificationContext();
  const { reloadWallet } = useDataLoadingContext(); // We can reuse the same trigger or add a new one
  const [data, setData] = useState<ITableData<IPassengerWallet>>(defaultTableData);

  const [debouncedSearch, setDebouncedSearch] = useState(data.search);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(data.search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [data.search]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [data.page, debouncedSearch, reloadWallet]);

  const fetchData = async () => {
    setData((prev) => ({ ...prev, loading: true }));
    const { data: response, error } = await passengerWalletsApi(
      data.page,
      data.per_page,
      data.search,
      true // Enable Local Caching
    );
    if (response) {
      response.data = response.data.map((value) => ({
        ...value,
        key: value.id,
      }));
      setData((prev) => ({ ...prev, ...response }));
    } else showError(error!.message);
    setData((prev) => ({ ...prev, loading: false }));
  };

  const handlePageChange = ({ current }: TablePaginationConfig) => {
    if (current) setData((prev) => ({ ...prev, page: current }));
  };

  const handleSearch = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, search: value ?? "", page: 1 }));
  };

  return { data, handlePageChange, handleSearch };
}
